import React, { useContext, useEffect, useState } from "react";
import { FirstInputFormDataContext } from "../../../context/FirstInputFormDataContext.js";
import { SecondInputFormDataContext } from "../../../context/SecondInputFormDataContext.js";
import { SpecialInputContext } from "./SpecialInputContext.js";
import SpecialInputMemberContainer from "./SpecialInputMemberContainer/SpecialInputMemberContainer.js";

// styles
import Style from "./SpecialInputFirstInputForm.module.css";

const SpecialInputForm = ({ name, question, formType }) => {
  // Determine the context to use based on formType
  const { projectTeam, setProjectTeam } = useContext(
    formType === "first"
      ? FirstInputFormDataContext
      : SecondInputFormDataContext
  );

  // project members
  const [projectMembers, setProjectMembers] = useState([]);
  const [addMemberFormIsActive, setAddMemberFormIsActive] = useState(false);
  /*   const [deleteMemberIsActive, setDeleteMemberIsActive] = useState(false); */
  // New state for editing
  const [editMemberIndex, setEditMemberIndex] = useState(-1);
  const [editedMember, setEditedMember] = useState(null);

  // when component mounts
  useEffect(() => {
    const sessionStorageProjectMembers = sessionStorage.getItem(name);

    // preventing data from disappearing after refreshing page
    if (sessionStorageProjectMembers) {
      const serializedState = JSON.parse(sessionStorageProjectMembers);
      setProjectTeam(serializedState);
    }
  }, [name, setProjectTeam]);

  // add new member
  const addNewMember = (newMember) => {
    setProjectTeam((prevMembers) => {
      // Use the updated state to ensure correctness
      const updatedMembers = [...prevMembers, newMember];

      // Store the updated state in sessionStorage
      const serializedState = JSON.stringify(updatedMembers);
      sessionStorage.setItem(name, serializedState);

      // Return the new state
      return updatedMembers;
    });
  };

  // delete member
  const deleteMember = (memberId) => {
    setProjectTeam((prevMembers) => {
      const updatedMembers = [...prevMembers];
      updatedMembers.splice(memberId, 1);
      sessionStorage.setItem(name, JSON.stringify(updatedMembers));
      return updatedMembers;
    });
  };

  // start editing a member
  const startEditingMember = (index) => {
    setEditMemberIndex(index);
    setEditedMember({ ...projectTeam[index] });
    setAddMemberFormIsActive(false);
  };

  // save edited member
  const saveEditedMember = () => {
    setProjectTeam((prevMembers) => {
      const updatedMembers = [...prevMembers];
      updatedMembers[editMemberIndex] = editedMember;

      // Update sessionStorage
      sessionStorage.setItem(name, JSON.stringify(updatedMembers));
      return updatedMembers;
    });

    // Reset edit state
    setEditMemberIndex(-1);
    setEditedMember(null);
  };

  // cancel editing
  const cancelEditing = () => {
    setEditMemberIndex(-1);
    setEditedMember(null);
  };

  // handle edited member data changes
  const handleEditedMemberChange = (e) => {
    const { name, value } = e.target;
    setEditedMember((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // handle other project changes
  const handleProjectChange = (index, field, value) => {
    const updatedProjects = [...editedMember.otherProjects];
    updatedProjects[index] = {
      ...updatedProjects[index],
      [field]: value,
    };

    setEditedMember((prev) => ({
      ...prev,
      otherProjects: updatedProjects,
    }));
  };

  // add new other project
  const addOtherProject = () => {
    setEditedMember((prev) => ({
      ...prev,
      otherProjects: [
        ...prev.otherProjects,
        { otherProjectName: "", otherProjectPercentage: 0 },
      ],
    }));
  };

  // remove other project
  const removeOtherProject = (index) => {
    setEditedMember((prev) => ({
      ...prev,
      otherProjects: prev.otherProjects.filter((_, i) => i !== index),
    }));
  };

  function manageInputForm() {
    if (editMemberIndex !== -1) {
      return; // Don't toggle form if in edit mode
    }

    setAddMemberFormIsActive(!addMemberFormIsActive);
    sessionStorage.setItem("member_form_is_active", addMemberFormIsActive);
  }

  return (
    <SpecialInputContext.Provider value={{ projectMembers, setProjectMembers }}>
      <div className={Style.SpecialInputContainer}>
        <p className="question">{question}</p>

        {/* INPUT FORM FOR NEW MEMBER */}
        {addMemberFormIsActive && editMemberIndex === -1 && (
          <SpecialInputMemberContainer addProjectMember={addNewMember} />
        )}

        {/* Don't show add button while editing */}
        {editMemberIndex === -1 && (
          <button
            onClick={manageInputForm}
            className={
              addMemberFormIsActive
                ? Style.SpecialInputMinus
                : Style.SpecialInputPlus
            }
          >
            {addMemberFormIsActive ? "SAKRIJ" : "DODAJ NOVOG ČLANA"}
          </button>
        )}

        {/* EDIT MEMBER FORM */}
        {editMemberIndex !== -1 && editedMember && (
          <div className={Style.EditMemberForm}>
            <h3>Uredi člana</h3>

            <div className={Style.EditFormField}>
              <label>Ime i prezime:</label>
              <input
                type="text"
                name="nameSurname"
                value={editedMember.nameSurname}
                onChange={handleEditedMemberChange}
                className={Style.EditInput}
              />
            </div>

            <div className={Style.EditFormField}>
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={editedMember.email}
                onChange={handleEditedMemberChange}
                className={Style.EditInput}
              />
            </div>

            <div className={Style.EditFormField}>
              <label>Postotak rada na projektu:</label>
              <input
                type="number"
                name="thisProjectPercentage"
                value={editedMember.thisProjectPercentage}
                onChange={handleEditedMemberChange}
                className={Style.EditInput}
                min="0"
                max="100"
              />
              %
            </div>

            <div className={Style.ProjectOtherProjectsTitle}>
              OSTALI PROJEKTI:
              <button
                onClick={addOtherProject}
                className={Style.AddProjectButton}
              >
                + Dodaj projekt
              </button>
            </div>

            {editedMember.otherProjects.map((project, idx) => (
              <div key={idx} className={Style.EditOtherProject}>
                <div className={Style.EditProjectField}>
                  <label>Naziv projekta:</label>
                  <input
                    type="text"
                    value={project.otherProjectName}
                    onChange={(e) =>
                      handleProjectChange(
                        idx,
                        "otherProjectName",
                        e.target.value
                      )
                    }
                    className={Style.EditInput}
                  />
                </div>

                <div className={Style.EditProjectField}>
                  <label>Postotak:</label>
                  <input
                    type="number"
                    value={project.otherProjectPercentage}
                    onChange={(e) =>
                      handleProjectChange(
                        idx,
                        "otherProjectPercentage",
                        e.target.value
                      )
                    }
                    className={Style.EditInput}
                    min="0"
                    max="100"
                  />
                  %
                </div>

                <button
                  onClick={() => removeOtherProject(idx)}
                  className={Style.RemoveProjectButton}
                >
                  Ukloni
                </button>
              </div>
            ))}

            <div className={Style.EditActions}>
              <button onClick={saveEditedMember} className={Style.SaveButton}>
                Spremi promjene
              </button>
              <button onClick={cancelEditing} className={Style.CancelButton}>
                Odustani
              </button>
            </div>
          </div>
        )}

        {/* ALL MEMBERS - PRINTING ALL MEMBERS */}
        <div className={Style.SpecialInputCompletedMembers}>
          {/*   <div style={{ display: "flex", flexDirection: "row" }}>
            <p style={{ margin: "0px" }}>Svi članovi:</p>
            {projectTeam?.length > 0 && editMemberIndex === -1 && (
              <button
                onClick={() => setDeleteMemberIsActive(!deleteMemberIsActive)}
                className={Style.EditProjectTeamMembers}
              >
                {deleteMemberIsActive ? "ZAVRŠI UREĐIVANJE" : "UREDITE ČLANOVE"}
              </button>
            )}
          </div> */}

          {projectTeam?.length > 0 ? (
            projectTeam?.map((member, index) => (
              <div key={index} className={Style.ProjectTeamMember}>
                <div className={Style.ProjectTeamMemberInfo}>
                  <div style={{ marginRight: "10px" }}>
                    {member.nameSurname}
                  </div>
                  <div style={{ marginRight: "10px" }}>({member.email})</div>
                  <div> {member.thisProjectPercentage}%</div>

                  {/* Show edit/delete buttons based on mode */}
                </div>

                <div className={Style.ProjectOtherProjectsTitle}>
                  OSTALI PROJEKTI:
                </div>
                {member.otherProjects.length > 0 ? (
                  member.otherProjects.map((project, indexProject) => (
                    <div
                      key={indexProject}
                      className={Style.ProjectTeamMemberOtherProjects}
                    >
                      <div className={Style.ProjectTeamOtherProjectInfo}>
                        {project.otherProjectName}{" "}
                        {project.otherProjectPercentage}%
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ margin: "8px 0px 8px 10px" }}>
                    niste dodali niti jedan projekt ovom članu
                  </p>
                )}

                <div className={Style.ButtonsContainer}>
                  <div
                    style={{
                      fontWeight: "400",
                      textDecoration: "underline",
                      border: "none",
                    }}
                  >
                    <button
                      onClick={() => startEditingMember(index)}
                      className={Style.EditButton}
                    >
                      UREDI
                    </button>
                  </div>
                  <div
                    style={{
                      fontWeight: "400",
                      textDecoration: "underline",
                      border: "none",
                    }}
                  >
                    <button
                      onClick={() => deleteMember(index)}
                      className={Style.DeleteMemberYesNo}
                    >
                      UKLONI
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>niste dodali niti jednog člana...</p>
          )}
        </div>
      </div>
    </SpecialInputContext.Provider>
  );
};

export default SpecialInputForm;
